"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  ReceiptTextIcon,
} from "lucide-react";

import type { FacturationRule } from "@/prisma/generated/client";
import {
  invalidateFacturationRulesQuery,
  useFacturationRulesQuery,
} from "@/lib/queries";
import { deleteFacturationRule } from "@/app/lib/actions/facturation-rules";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import FacturationRuleForm from "./facturation-rule-form";

function formatUnitCost(value: FacturationRule["unitCost"]): string {
  const normalized = Number(value.toString());
  return Number.isNaN(normalized)
    ? value.toString()
    : normalized.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
}

function AdministrationFacturationRules() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useFacturationRulesQuery({
    sort: { createdAt: "desc" },
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FacturationRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<FacturationRule | null>(
    null,
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refresh = () => invalidateFacturationRulesQuery(queryClient);

  const openCreate = () => {
    setEditingRule(null);
    setFormOpen(true);
  };

  const openEdit = (rule: FacturationRule) => {
    setEditingRule(rule);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRule) return;
    setDeleteLoading(true);
    await deleteFacturationRule({ id: deletingRule.id });
    setDeleteLoading(false);
    setDeletingRule(null);
    refresh();
  };

  const rules = data?.facturationRules ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ReceiptTextIcon className="text-primary size-5" />
                Facturation opérations{" "}
                {!isLoading ? (
                  <span className="text-muted-foreground text-sm font-normal">
                    ({total} entrées)
                  </span>
                ) : null}
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Paramétrage FDM / immatriculation avec historisation et
                versionnement.
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Nouvelle règle
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isError ? (
            <p className="px-6 py-4 text-sm text-red-600">
              Erreur lors du chargement des règles de facturation.
            </p>
          ) : null}

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">M. num</TableHead>
                  <TableHead className="w-20">M. alpha</TableHead>
                  <TableHead>Libellé marque</TableHead>
                  <TableHead className="w-24">Code</TableHead>
                  <TableHead>Type service</TableHead>
                  <TableHead>Poste notice</TableHead>
                  <TableHead className="w-24">Article</TableHead>
                  <TableHead className="w-28 text-right">Coût</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-mono">
                      {rule.brandNumericCode}
                    </TableCell>
                    <TableCell className="font-mono">
                      {rule.brandAlphabeticCode}
                    </TableCell>
                    <TableCell>{rule.brandAlphabeticLabel}</TableCell>
                    <TableCell className="font-mono">
                      {rule.billingCode}
                    </TableCell>
                    <TableCell>{rule.serviceType}</TableCell>
                    <TableCell>{rule.noticeItemLabel}</TableCell>
                    <TableCell className="font-mono">
                      {rule.articleNumber}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatUnitCost(rule.unitCost)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary size-7"
                          onClick={() => openEdit(rule)}
                        >
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7"
                          onClick={() => setDeletingRule(rule)}
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-muted-foreground py-6 text-center"
                    >
                      Aucune règle active.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}

          {!isLoading && rules.length > 0 ? (
            <div className="text-muted-foreground border-t px-6 py-3 text-xs">
              {total} règles actives · Versionnement actif
            </div>
          ) : null}
        </CardContent>
      </Card>

      <FacturationRuleForm
        key={editingRule?.id ?? "new"}
        rule={editingRule}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
      />

      <AlertDialog
        open={!!deletingRule}
        onOpenChange={(isOpen) => !isOpen && setDeletingRule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la règle ?</AlertDialogTitle>
            <AlertDialogDescription>
              La règle sera désactivée (soft delete) et historisée via le
              versionnement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { AdministrationFacturationRules };
